import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: ReactNode;
  /** Right-aligned action controls (buttons, links). */
  actions?: ReactNode;
  className?: string;
}

/**
 * List-screen header.
 *
 * The title is announced, not drawn. The sidebar already says which screen you
 * are on — and says it clearly now that the current row is solid — so a 22px
 * restatement of the same word cost a band of vertical space above every
 * table for nothing. Admin puts the page name in its top bar for the same
 * reason: it does not belong above the data.
 *
 * The `<h1>` stays as the document's heading so screen readers and the
 * heading outline still work.
 */
export function PageHeader({ title, actions, className }: PageHeaderProps) {
  return (
    <>
      {/*
        No wrapper when there is nothing to draw. A zero-height div is still a
        flex child, so it claimed a full gap above every table; the sr-only
        <h1> is absolutely positioned and claims none.
      */}
      <h1 className="sr-only">{title}</h1>
      {actions && (
        <div className={cn("flex items-center justify-end gap-2", className)}>
          {actions}
        </div>
      )}
    </>
  );
}
